import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockVendorManagementService } from '@/services/mock/MockVendorManagementService';
import { VendorRecord } from '@/types/domain/vendor-management';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ArrowLeft, Save, Building2, User, CreditCard, ShieldCheck, Truck } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

export const VendorEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState<VendorRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form State
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [category, setCategory] = useState('');
  const [vendorType, setVendorType] = useState('');
  const [industry, setIndustry] = useState('');

  // Primary Contact
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactRole, setContactRole] = useState('');

  // Commercial & Financial Terms
  const [paymentTerms, setPaymentTerms] = useState('');
  const [incoterms, setIncoterms] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [bankName, setBankName] = useState('');

  useEffect(() => {
    const loadVendor = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const data = await mockVendorManagementService.getVendorById(id);
        if (!data) {
          navigate(ROUTES.VENDORS.LIST);
          return;
        }
        setVendor(data);
        setName(data.name || '');
        setLegalName(data.legalName || '');
        setCategory(data.category || 'IT Hardware');
        setVendorType(data.vendorType || 'OEM Manufacturer');
        setIndustry(data.industry || 'Information Technology');

        setContactName(data.primaryContact?.name || '');
        setContactEmail(data.primaryContact?.email || '');
        setContactPhone(data.primaryContact?.phone || '');
        setContactRole(data.primaryContact?.role || 'Account Director');

        setPaymentTerms(data.commercial?.paymentTerms || 'Net 30');
        setIncoterms(data.commercial?.incoterms || 'DDP');
        setGstNumber(data.commercial?.gstNumber || 'GST29AAAAA0000A1Z5');
        setPanNumber(data.commercial?.panNumber || 'ABCDE1234F');
        setBankName(data.commercial?.bankName || 'HDFC Bank');
      } finally {
        setIsLoading(false);
      }
    };

    loadVendor();
  }, [id, navigate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !vendor) return;

    try {
      setIsSaving(true);
      await mockVendorManagementService.updateVendor(id, {
        name,
        legalName,
        category,
        vendorType,
        industry,
        primaryContact: {
          ...vendor.primaryContact,
          name: contactName,
          email: contactEmail,
          phone: contactPhone,
          role: contactRole,
        },
        commercial: {
          ...vendor.commercial,
          currency: 'INR',
          paymentTerms,
          incoterms,
          gstNumber,
          panNumber,
          bankName,
        },
      });
      navigate(ROUTES.VENDORS.DETAILS(id));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update vendor record.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Vendor Master Editor...
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="p-16 text-center space-y-4 font-poppins">
        <h2 className="text-lg font-bold text-foreground">Vendor Record Not Found</h2>
        <Button size="sm" onClick={() => navigate(ROUTES.VENDORS.LIST)} className="font-poppins cursor-pointer">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Vendor Directory
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 font-poppins max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.VENDORS.DETAILS(id!))}
            className="rounded-xl border-border hover:bg-muted font-poppins cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-primary px-2.5 py-0.5 bg-primary/10 rounded-lg border border-primary/20">
                {vendor.vendorCode}
              </span>
              <h1 className="text-xl font-extrabold text-foreground tracking-tight font-poppins">
                Edit Vendor Master Record
              </h1>
            </div>
            <p className="text-xs text-foreground-secondary font-medium mt-0.5 font-poppins">
              Legal Entity: <strong className="text-foreground">{vendor.legalName}</strong> • SAP Ref: {vendor.sapVendorCode || 'N/A'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.VENDORS.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-vendor-form"
            size="sm"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </div>

      <form id="edit-vendor-form" onSubmit={handleSave} className="space-y-6">
        {/* Section 1: Corporate Profile & Classification */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <Building2 className="h-4 w-4 text-primary" />
              Supplier Identification & Categorization
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Supplier Trade Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground font-medium focus:ring-2 focus:ring-primary font-poppins"
                  placeholder="e.g. Apex Hardware Solutions"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Registered Legal Entity Name *
                </label>
                <input
                  type="text"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground font-medium focus:ring-2 focus:ring-primary font-poppins"
                  placeholder="e.g. Apex Hardware Solutions Pvt. Ltd."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Procurement Category *
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="IT Hardware">IT Hardware</option>
                  <option value="Facilities & Services">Facilities & Services</option>
                  <option value="Consulting Services">Consulting Services</option>
                  <option value="Raw Materials">Raw Materials</option>
                  <option value="Capital Equipment">Capital Equipment</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Supplier Business Type *
                </label>
                <select
                  value={vendorType}
                  onChange={(e) => setVendorType(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="OEM Manufacturer">OEM Manufacturer</option>
                  <option value="Authorized Distributor">Authorized Distributor</option>
                  <option value="Service Provider">Service Provider</option>
                  <option value="Turnkey Contractor">Turnkey Contractor</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Industry Classification *
                </label>
                <input
                  type="text"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                  placeholder="e.g. Information Technology"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Primary Account Representative */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <User className="h-4 w-4 text-primary" />
              Primary Corporate Account Representative
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Contact Person Name *
                </label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                  placeholder="e.g. Rajesh Sharma"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-medium text-foreground focus:ring-2 focus:ring-primary font-poppins"
                  placeholder="contact@supplier.com"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-medium text-foreground focus:ring-2 focus:ring-primary font-poppins"
                  placeholder="+91 98765 43210"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Designation / Role
                </label>
                <input
                  type="text"
                  value={contactRole}
                  onChange={(e) => setContactRole(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-medium text-foreground focus:ring-2 focus:ring-primary font-poppins"
                  placeholder="e.g. Account Director"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Commercial Baseline & Tax Compliance */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <CreditCard className="h-4 w-4 text-primary" />
              Commercial Settlement & Statutory Compliance (India)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Default Payment Terms *
                </label>
                <select
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="Net 30">Net 30 Days</option>
                  <option value="Net 45">Net 45 Days</option>
                  <option value="Net 60">Net 60 Days</option>
                  <option value="20% Advance, 80% on Delivery">20% Advance, 80% on Delivery</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Truck className="h-3.5 w-3.5 text-primary" /> Delivery Incoterms *
                </label>
                <select
                  value={incoterms}
                  onChange={(e) => setIncoterms(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="DDP">DDP — Delivered Duty Paid</option>
                  <option value="DAP">DAP — Delivered At Place</option>
                  <option value="FOB">FOB — Free On Board</option>
                  <option value="CIF">CIF — Cost, Insurance & Freight</option>
                  <option value="EXW">EXW — Ex Works</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> GSTIN Number *
                </label>
                <input
                  type="text"
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-mono font-bold text-foreground focus:ring-2 focus:ring-primary uppercase"
                  placeholder="29AAAAA0000A1Z5"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  PAN Identification Number *
                </label>
                <input
                  type="text"
                  value={panNumber}
                  onChange={(e) => setPanNumber(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-mono font-bold text-foreground focus:ring-2 focus:ring-primary uppercase"
                  placeholder="ABCDE1234F"
                />
              </div>
            </div>

            <div>
              <label className="block font-bold text-foreground mb-1.5">
                Designated Disbursement Bank
              </label>
              <input
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary"
                placeholder="e.g. HDFC Bank Ltd. / State Bank of India"
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Controls */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.VENDORS.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer px-6 shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};
